import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CognitoUserSession, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { QRCodeSVG } from 'qrcode.react';
import { logger } from '../utils/logger';
import { getCognitoUserPool } from '../utils/cognitoPool';

interface TOTPSetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

const TOTPSetup: React.FC<TOTPSetupProps> = ({ onComplete, onCancel }) => {
  const theme = useTheme();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secretCode, setSecretCode] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'loading' | 'scan' | 'verify'>('loading');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [showManualCode, setShowManualCode] = useState(false);

  // Use the shared singleton CognitoUserPool
  const userPool = useMemo(() => getCognitoUserPool(), []);

  const initTOTP = () => {
    setError(null);
    setIsExpired(false);
    setStep('loading');
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        setError('No authenticated user found. Please log in again.');
        setIsExpired(true);
        setStep('scan');
        return;
      }

      // Get session to ensure we're authenticated
      cognitoUser.getSession((sessionErr: Error | null, session: CognitoUserSession | null) => {
        if (sessionErr || !session?.isValid()) {
          setError('Session expired. Please log in again.');
          setIsExpired(true);
          setStep('scan');
          return;
        }

        // Start TOTP setup
        cognitoUser.associateSoftwareToken({
          associateSecretCode: (code: string) => {
            setSecretCode(code);

            // Get user email for QR code issuer
            cognitoUser.getUserAttributes((attrErr: Error | null, attributes?: CognitoUserAttribute[]) => {
              const email = attributes?.find((attr: CognitoUserAttribute) => attr.getName() === 'email')?.getValue() || cognitoUser.getUsername();
              const qrUrl = `otpauth://totp/LegacyCatalog:${email}?secret=${code}&issuer=LegacyCatalog`;
              setQrCodeUrl(qrUrl);
              setStep('scan');
            });
          },
          onFailure: (err: Error) => {
            logger.error('TOTP setup error', err);
            setError(err.message || 'Failed to set up authenticator app');
            setIsExpired(true);
            setStep('scan');
          },
        });
      });
    } catch (err) {
      logger.error('TOTP setup error', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to set up authenticator app';
      setError(errorMessage);
      setIsExpired(true);
      setStep('scan');
    }
  };

  useEffect(() => {
    initTOTP();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPool]);

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) return;

    setLoading(true);
    setError(null);

    try {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        setError('No authenticated user found. Please log in again.');
        setLoading(false);
        return;
      }

      // Get session to ensure we're authenticated
      cognitoUser.getSession((sessionErr: Error | null, session: CognitoUserSession | null) => {
        if (sessionErr || !session?.isValid()) {
          setError('Session expired. Please log in again.');
          setLoading(false);
          return;
        }

        // Verify TOTP code
        cognitoUser.verifySoftwareToken(verificationCode, 'Authenticator App', {
          onSuccess: async () => {
            // Set TOTP as preferred MFA method
            cognitoUser.setUserMfaPreference(
              null, // SMS - not used
              { Enabled: true, PreferredMfa: true }, // TOTP
              (_mfaErr: Error | null) => {
                // Continue even if MFA preference setting fails - TOTP is still enabled
                setLoading(false);
                onComplete();
              }
            );
          },
          onFailure: (err: Error) => {
            logger.error('TOTP verification failed', err);
            setError(err.message || 'Invalid verification code. Please try again.');
            setLoading(false);
          },
        });
      });
    } catch (err) {
      logger.error('TOTP verification error', err);
      const errorMessage = err instanceof Error ? err.message : 'Verification failed. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Setting up authenticator app...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Authenticator App Setup
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator to generate verification codes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isExpired && (
        <Button
          variant="contained"
          onClick={initTOTP}
          sx={{ mb: 2 }}
        >
          Retry Setup
        </Button>
      )}

      {qrCodeUrl ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Step 1:</strong> Scan the QR code below with your authenticator app
            </Typography>
          </Alert>

          <Box sx={{ textAlign: 'center', my: 3, p: 2, display: 'inline-block', bgcolor: 'white', borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }} data-testid="totp-qr-code">
            <QRCodeSVG
              value={qrCodeUrl}
              size={200}
              level="M"
              includeMargin={false}
              role="img"
              aria-label="QR Code for authenticator app"
            />
          </Box>

          {!showManualCode && (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowManualCode(true)}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Can&apos;t scan? Show manual code
            </Button>
          )}

          {showManualCode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Can&apos;t scan?</strong> Enter this code manually: <code style={{ userSelect: 'all' }}>{secretCode}</code>
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            sx={{ mb: 3 }}
            autoFocus
            disabled={loading}
          />
        </>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unable to generate QR code. Please try again or use a security key instead.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          fullWidth
          disabled={loading}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleVerify}
          fullWidth
          disabled={!verificationCode || verificationCode.length !== 6 || loading || !qrCodeUrl}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
      </Box>
    </Box>
  );
};

export default TOTPSetup;
